import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofActionCompleted, ofActionSuccessful, Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { distinctUntilChanged, merge, Observable, Subject, takeUntil } from 'rxjs';
import { AuthActions } from '@shared/store/auth/auth.actions';
import { ApplicationActions } from '@shared/store/application/application.actions';
import { NgxSpinnerService } from 'ngx-spinner';
import { LoaderState } from '@shared/store/loader/loader.state';
import { delay, filter, switchMap, take } from 'rxjs/operators';
import { AuthSelectors } from '@shared/store/auth/auth.selectors';
import { AssignPatientToTimelineFacadeService } from '@shared/service/assign-patient-to-timeline-facade.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private onDestroy$: Subject<void> = new Subject<void>();

  private loader$: Observable<boolean> = this.store.select<boolean>(LoaderState.uploading);

  constructor(private readonly router: Router,
              private readonly actions: Actions,
              private readonly spinner: NgxSpinnerService,
              private readonly store: Store,
              private readonly assignPatientToTimelineFacadeService: AssignPatientToTimelineFacadeService) {
  }

  ngOnInit() {
    merge(
      this.actions.pipe(
        ofActionSuccessful(AuthActions.Login)
      )
    ).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(() =>  this.router.navigate([ '/' ]));

    this.store.select(AuthSelectors.getCurrentLogged).pipe(
      take(1),
      takeUntil(this.onDestroy$),
      filter(Boolean),
      filter(account => account.showPatientAssignmentBanner),
      switchMap(account => this.assignPatientToTimelineFacadeService.showAssignPatientToTimelineModal(account))
    ).subscribe();

    this.store.select(AuthSelectors.getCurrentLogged).pipe(
      takeUntil(this.onDestroy$),
      filter(Boolean),
    ).subscribe(account => {
      this.updateMenu(account.isAdmin, account.isSuperUser);
    });

    this.actions.pipe(
      ofActionCompleted(AuthActions.Logout),
      takeUntil(this.onDestroy$)
    ).subscribe(() => this.router.navigate([ '/auth/login' ]));


    this.loader$.pipe(
      takeUntil(this.onDestroy$),
      distinctUntilChanged(),
      delay(0)
    ).subscribe((loading: boolean) => loading ? this.spinner.show('main') : this.spinner.hide('main'));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  @HostListener('window:resize', [ '$event' ])
  onResize() {
    this.store.dispatch(new ApplicationActions.SetWindowSize(window.innerWidth));
  }

  updateMenu(isAdmin: boolean, isSuperUser: boolean): void {
    if (isSuperUser) {
      this.store.dispatch(new ApplicationActions.UpdateMenu([
        { title: 'PATIENTS', icon: 'users-group', link: '/patients', routerLinkExact: false },
        { title: 'CALENDAR', icon: 'calendar', link: '/calendar', routerLinkExact: false },
        { title: 'TEAM', icon: 'users', link: '/team', routerLinkExact: false },
        { title: 'SELF_HELP', icon: 'help-hexagon', link: '/self-help-library', routerLinkExact: false },
        { title: 'HOLIDAYS_AND_LEAVES', icon: 'calendar-plus', link: '/timeout', routerLinkExact: false, isHolidaysAndLeaves: true },
        { title: 'LOG_BOOK', icon: 'book-2', link: '/log-book', routerLinkExact: false },
        { title: 'BUDDY_GROUPS', icon: 'friends', link: '/buddy-groups', routerLinkExact: false },
      ]));
    } else if (isAdmin) {
      this.store.dispatch(new ApplicationActions.UpdateMenu([
        { title: 'PATIENTS', icon: 'users-group', link: '/patients', routerLinkExact: false },
        { title: 'TEAM', icon: 'users', link: '/team', routerLinkExact: false },
        { title: 'SELF_HELP', icon: 'help-hexagon', link: '/self-help-library', routerLinkExact: false },
        { title: 'BUDDY_GROUPS', icon: 'friends', link: '/buddy-groups', routerLinkExact: false },
        { title: 'HOLIDAYS_AND_LEAVES', icon: 'calendar-plus', link: '/timeout', routerLinkExact: false, isHolidaysAndLeaves: true }
      ]));
    } else {
      this.store.dispatch(new ApplicationActions.UpdateMenu([
        { title: 'HOME', icon: 'home', link: '/', routerLinkExact: true },
        { title: 'CALENDAR', icon: 'calendar', link: '/calendar', routerLinkExact: false },
        { title: 'PATIENTS', icon: 'users-group', link: '/patients', routerLinkExact: false },
        { title: 'LOG_BOOK', icon: 'book-2', link: '/log-book', routerLinkExact: false },
        { title: 'HOLIDAYS_AND_LEAVES', icon: 'calendar-plus', link: '/timeout', routerLinkExact: false }
      ]));
    }
  }
}